import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { useT } from "../../i18n/locale";
import {
  chooseAudio,
  deviceName,
  looksBuiltIn,
  outputSelectionSupported,
  shortDeviceName,
  SYSTEM_DEVICE_IDS,
  useAudioChoice,
} from "../../telephony/audio-devices";

// Il microfono e l'ascolto nella testata della «Postazione di chiamata», accanto a chi chiama (richiesta del product owner
// del 6 ottobre 2026: «nelle nostre impostazioni di chiamata, possiamo scegliere qual è il microfono che utilizziamo per
// telefonare»; il posto l'ha scelto lui: «Nella postazione»). Il pulsante dice il microfono in uso e apre, sotto la testata,
// un pannello con «Microfono» e, dove il browser sa mandare l'audio a un dispositivo scelto (Chrome sì, Safari no),
// «Ascolto». La scelta resta su questo computer e vale per ogni chiamata della postazione, Power Dialer compreso, anche a
// chiamata in corso (`telephony/audio-devices.ts`). Prima del permesso del microfono il browser non dice i nomi: il pannello
// lo spiega e offre «Consenti il microfono», che chiede il permesso una volta e spegne subito il microfono. Un dispositivo
// scelto che non è collegato fa usare quello di sistema, e il pannello lo dice, con l'avviso legato alla sua tendina e
// annunciato quando compare: l'avviso arriva dentro una regione `role="status"` che sotto ogni tendina esiste già, vuota,
// prima che il testo arrivi (alcuni lettori di schermo non leggono un elemento nato insieme al suo testo); il microfono del
// computer ha un suggerimento gentile, soltanto dove si chiama (`hint`). Nessuno dei due ferma mai una chiamata (regola dei
// pochi blocchi del 5 ottobre 2026). Le parole sono proposte (questione 216).
// Il pannello si chiude con Esc, dal pulsante o da dentro il pannello (il focus torna al pulsante), o con il pulsante stesso.

/** `navigator.mediaDevices`, oppure `null` dove il browser non lo dà (una pagina non sicura, un browser vecchio). */
function browserMediaDevices(): MediaDevices | null {
  const media = (navigator as { mediaDevices?: MediaDevices }).mediaDevices;
  return media && typeof media.enumerateDevices === "function" ? media : null;
}

type DeviceList = { inputs: MediaDeviceInfo[]; outputs: MediaDeviceInfo[] };

/** I microfoni e gli ascolti del browser, riletti quando cambiano (`devicechange`); `allow` chiede il permesso del microfono. */
function useDeviceList(media: MediaDevices | null) {
  const [list, setList] = useState<DeviceList | null>(null);
  const [denied, setDenied] = useState(false);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    if (!media) return;
    let devices: MediaDeviceInfo[];
    try {
      devices = await media.enumerateDevices();
    } catch {
      devices = [];
    }
    if (!mounted.current) return;
    setList({ inputs: devices.filter((device) => device.kind === "audioinput"), outputs: devices.filter((device) => device.kind === "audiooutput") });
  }, [media]);

  useEffect(() => {
    mounted.current = true;
    if (!media) return;
    const changed = () => void refresh();
    changed();
    media.addEventListener("devicechange", changed);
    return () => {
      mounted.current = false;
      media.removeEventListener("devicechange", changed);
    };
  }, [media, refresh]);

  /** Il permesso, una volta: il microfono si accende e si spegne subito, poi i nomi si rileggono. */
  const allow = useCallback(async () => {
    if (!media) return;
    try {
      const stream = await media.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      if (mounted.current) setDenied(false);
    } catch {
      if (mounted.current) setDenied(true);
    }
    await refresh();
  }, [media, refresh]);

  return { list, denied, refresh, allow };
}

/** Il dispositivo che si usa: quello scelto se c'è, altrimenti quello di sistema («default», o il primo). */
function inUse(devices: readonly MediaDeviceInfo[], chosen: string | null): MediaDeviceInfo | null {
  const picked = chosen === null ? undefined : devices.find((device) => device.deviceId === chosen);
  return picked ?? devices.find((device) => device.deviceId === "default") ?? devices[0] ?? null;
}

/** Il dispositivo scelto non è fra quelli collegati (lo si sa soltanto quando il browser dice i nomi). */
function chosenMissing(devices: readonly MediaDeviceInfo[], chosen: string | null, named: boolean): boolean {
  return chosen !== null && named && !devices.some((device) => device.deviceId === chosen);
}

/**
 * `hint`: il suggerimento sul microfono del computer, soltanto dove la postazione chiama o segue una chiamata (non accanto
 * a «Chiama» spento).
 */
export function AudioDevicesControl({ hint }: { hint: boolean }) {
  const t = useT();
  const [media] = useState(browserMediaDevices);
  const [canChooseOutput] = useState(outputSelectionSupported);
  const choice = useAudioChoice();
  const { list, denied, refresh, allow } = useDeviceList(media);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const inputId = useId();
  const outputId = useId();
  const inputMissingId = useId();
  const outputMissingId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Aperto, Esc dentro il pannello lo chiude e il focus torna al pulsante. Il listener sta qui e non nel JSX perché il
  // pannello non è un controllo.
  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    };
    panel.addEventListener("keydown", handleKeyDown);
    return () => panel.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const inputs = list?.inputs ?? [];
  const outputs = list?.outputs ?? [];
  // Prima del permesso il browser non dice i nomi (né gli identificativi veri) dei dispositivi.
  const named = inputs.some((device) => device.label !== "");
  const microphone = inUse(inputs, choice.input);
  const microphoneName = microphone ? deviceName(microphone) : "";
  const builtIn = hint && named && microphone !== null && looksBuiltIn(microphone.label);
  const inputMissing = chosenMissing(inputs, choice.input, named);
  const outputMissing = canChooseOutput && chosenMissing(outputs, choice.output, named);

  function onButtonKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "Escape" || !open) return;
    event.stopPropagation();
    setOpen(false);
  }

  function toggle() {
    if (!open) void refresh();
    setOpen(!open);
  }

  /** Le voci di una tendina: «Predefinito di sistema» (con il nome, se il browser lo dice) e i dispositivi collegati. */
  function optionsOf(devices: readonly MediaDeviceInfo[]) {
    const system = devices.find((device) => device.deviceId === "default");
    const systemName = system ? deviceName(system) : "";
    const choosable = devices.filter((device) => device.deviceId !== "" && !SYSTEM_DEVICE_IDS.has(device.deviceId));
    return [
      { value: "", label: systemName === "" ? t("callDesk.audio.systemDefault") : t("callDesk.audio.systemDefaultNamed", { name: systemName }) },
      ...choosable.map((device, index) => ({ value: device.deviceId, label: deviceName(device) || t("callDesk.audio.unnamed", { number: index + 1 }) })),
    ];
  }

  /** Il valore della tendina: il dispositivo scelto se è collegato, altrimenti «Predefinito di sistema». */
  const valueOf = (devices: readonly MediaDeviceInfo[], chosen: string | null) => (chosen !== null && devices.some((device) => device.deviceId === chosen) ? chosen : "");

  let content;
  if (!media) {
    content = <p className="g-desk__hint">{t("callDesk.audio.unsupported")}</p>;
  } else if (list === null) {
    content = null;
  } else if (!named) {
    content = (
      <>
        <p className="g-desk__hint">{t("callDesk.audio.needsPermission")}</p>
        <div>
          <Button onClick={() => void allow()}>{t("callDesk.audio.allow")}</Button>
        </div>
        {denied ? (
          <Reason tone="warning" role="alert">
            {t("callDesk.audio.denied")}
          </Reason>
        ) : null}
      </>
    );
  } else {
    content = (
      <div className="g-desk__audio-fields">
        <div className="g-desk__audio-field">
          <Select
            id={inputId}
            label={t("callDesk.audio.microphone")}
            value={valueOf(inputs, choice.input)}
            options={optionsOf(inputs)}
            onChange={(value) => chooseAudio({ ...choice, input: value === "" ? null : value })}
            describedBy={inputMissing ? inputMissingId : undefined}
          />
          <div className="g-desk__audio-notice" role="status">
            {inputMissing ? (
              <Reason tone="warning" id={inputMissingId}>
                {t("callDesk.audio.inputMissing")}
              </Reason>
            ) : null}
          </div>
        </div>
        {canChooseOutput ? (
          <div className="g-desk__audio-field">
            <Select
              id={outputId}
              label={t("callDesk.audio.output")}
              value={valueOf(outputs, choice.output)}
              options={optionsOf(outputs)}
              onChange={(value) => chooseAudio({ ...choice, output: value === "" ? null : value })}
              describedBy={outputMissing ? outputMissingId : undefined}
            />
            <div className="g-desk__audio-notice" role="status">
              {outputMissing ? (
                <Reason tone="warning" id={outputMissingId}>
                  {t("callDesk.audio.outputMissing")}
                </Reason>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        className="g-desk__audio"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={microphoneName === "" ? t("callDesk.audio.title") : t("callDesk.audio.button", { name: microphoneName })}
        title={microphoneName === "" ? undefined : microphoneName}
        onClick={toggle}
        onKeyDown={onButtonKeyDown}
      >
        <Icon name="microphone" />
        <span className="g-desk__audio-name">{microphoneName === "" ? t("callDesk.audio.microphone") : shortDeviceName(microphoneName)}</span>
      </Button>
      {builtIn ? <Reason className="g-desk__audio-hint">{t("callDesk.audio.builtIn")}</Reason> : null}
      {open ? (
        <div ref={panelRef} id={panelId} className="g-desk__audio-panel" role="group" aria-label={t("callDesk.audio.title")}>
          {content}
        </div>
      ) : null}
    </>
  );
}
